import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  CircleDollarSign,
  CircleAlert,
  Landmark,
  Leaf,
  Info,
  LoaderCircle,
  ShieldCheck,
  ShoppingBag,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatUsd } from "../utils/money";
import { financeApi, type PledgeProduct } from "../api/finance";
import { useCopy } from "../i18n/copy";
import { useSession } from "../store/session";
import { RemoteNoticeDialog } from "../components/RemoteNoticeDialog";
import { configApi } from "../api/config";
import { Countdown } from "../components/Countdown";

export function FinancePage() {
  const { user, isPreview } = useSession();
  const { tr } = useCopy();
  const client = useQueryClient();
  const [tab, setTab] = useState<1 | 2>(1);
  const [selected, setSelected] = useState<PledgeProduct | null>(null);
  const [positionState, setPositionState] = useState<1 | 2>(1);
  const [completed, setCompleted] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const appConfig = useQuery({
    queryKey: ["app-config"],
    queryFn: configApi.app,
    refetchOnMount: "always",
  });
  const fundsEnabled = appConfig.data?.showFundsMenu === true;
  const stakingProducts = useQuery({
    queryKey: ["pledge-products", 1],
    queryFn: () => financeApi.products(1),
    enabled: !isPreview,
  });
  const fundProducts = useQuery({
    queryKey: ["pledge-products", 2],
    queryFn: () => financeApi.products(2),
    enabled: !isPreview && fundsEnabled,
  });
  const products = tab === 1 ? stakingProducts : fundProducts;
  const balance = useQuery({
    queryKey: ["pledge-balance", tab],
    queryFn: () => financeApi.balance(tab),
    enabled: !isPreview,
    retry: false,
  });
  const income = useQuery({
    queryKey: ["fund-income"],
    queryFn: financeApi.fundIncome,
    enabled: !isPreview && fundsEnabled && tab === 2,
  });

  useEffect(() => {
    if (appConfig.isFetched && !fundsEnabled && tab === 2) {
      setTab(1);
    }
  }, [appConfig.isFetched, fundsEnabled, tab]);
  const purchase = useMutation({
    mutationFn: () => financeApi.buy(selected!.id),
    onSuccess: () => {
      setCompleted(selected?.name || "");
      setSelected(null);
      client.invalidateQueries({ queryKey: ["pledge-products"] });
      client.invalidateQueries({ queryKey: ["pledge-balance"] });
      client.invalidateQueries({ queryKey: ["fund-income"] });
      client.invalidateQueries({ queryKey: ["session-user"] });
    },
  });
  const capital =
    tab === 1 ? Number(balance.data?.balance || 0) : user.fundBalance;
  const secondary =
    tab === 1
      ? Number(balance.data?.coin || 0)
      : Number(income.data?.profit || 0);
  const pledgePositions = balance.data?.positions || [];
  const visiblePositions = pledgePositions.filter(
    (position) => Number(position.state) === positionState,
  );

  return (
    <main className="page">
      <header className="page-title-row">
        <div>
          <p className="eyebrow">{tr("Planificación financiera")}</p>
          <h1>{tr("Finanzas")}</h1>
        </div>
        <button
          className="icon-action"
          aria-label={tr("Reglas")}
          onClick={() => setRulesOpen(true)}
        >
          <Info />
        </button>
      </header>
      <div className="finance-hero">
        <div>
          <p>{tr(tab === 1 ? "Capital disponible" : "Saldo de fondos")}</p>
          <strong>{formatUsd(capital)}</strong>
          <span>
            <TrendingUp /> {formatUsd(secondary)}{" "}
            {tr(tab === 1 ? "Saldo de moneda" : "Beneficio acumulado")}
          </span>
        </div>
        <span className="finance-emblem">
          {tab === 1 ? <Leaf /> : <Landmark />}
        </span>
      </div>
      <div className="segmented">
        <button className={tab === 1 ? "active" : ""} onClick={() => setTab(1)}>
          <Leaf /> Staking
        </button>
        {fundsEnabled && (
          <button
            className={tab === 2 ? "active" : ""}
            onClick={() => setTab(2)}
          >
            <Landmark /> {tr("Fondos")}
          </button>
        )}
      </div>
      {tab === 2 && (
        <div className="fund-balance-strip">
          <span>
            <small>{tr("Monto invertido")}</small>
            <strong>{formatUsd(Number(income.data?.buyAmount || 0))}</strong>
          </span>
          <span>
            <small>{tr("Saldo de moneda")}</small>
            <strong>{formatUsd(user.skpBalance)}</strong>
          </span>
        </div>
      )}
      {tab === 2 && (
        <Link
          className="button primary finance-deposit"
          to="/wallet/deposit?type=2"
        >
          {tr("Depositar en fondos")} <ArrowRight />
        </Link>
      )}
      {completed && (
        <div className="form-feedback success">
          <BadgeCheck /> {tr("Compra completada correctamente.")} {completed}
        </div>
      )}
      {!isPreview && (
        <section className="finance-positions">
          <div className="section-heading finance-position-heading">
            <div>
              <p className="eyebrow">{tr("Portafolio")}</p>
              <h2>{tr("Mis posiciones")}</h2>
            </div>
            <span className="finance-position-count">
              <BriefcaseBusiness />
              {pledgePositions.filter(
                (position) => Number(position.state) === 1,
              ).length}
            </span>
          </div>
          <div className="segmented finance-position-tabs">
            <button
              className={positionState === 1 ? "active" : ""}
              onClick={() => setPositionState(1)}
            >
              {tr("Activas")}
            </button>
            <button
              className={positionState === 2 ? "active" : ""}
              onClick={() => setPositionState(2)}
            >
              {tr("Finalizadas")}
            </button>
          </div>
          {balance.isLoading && (
            <div className="loading-row">
              <LoaderCircle className="spin" /> {tr("Cargando posiciones…")}
            </div>
          )}
          {!balance.isLoading &&
            (balance.isError || visiblePositions.length === 0) && (
              <div className="finance-position-empty">
                <img src="/empty/nodata.webp" alt="" />
                <strong>
                  {tr(
                    positionState === 1
                      ? "No tienes posiciones activas"
                      : "No tienes posiciones finalizadas",
                  )}
                </strong>
              </div>
            )}
          {visiblePositions.length > 0 && (
            <div className="finance-position-list">
              {visiblePositions.map((position) => (
                <article className="finance-position-card" key={position.id}>
                  <div className="finance-position-card-head">
                    <span
                      className={`state-pill ${Number(position.state) === 1 ? "done" : "pending"}`}
                    >
                      {tr(
                        Number(position.state) === 1
                          ? "En rendimiento"
                          : "Finalizada",
                      )}
                    </span>
                    <small>#{position.id}</small>
                  </div>
                  <h3>{position.name}</h3>
                  <div className="finance-position-values">
                    <span>
                      <small>{tr("Capital invertido")}</small>
                      <strong>{formatUsd(Number(position.money))}</strong>
                    </span>
                    <span>
                      <small>{tr("Rendimiento acumulado")}</small>
                      <strong>
                        {formatUsd(Number(position.current_profit))}
                      </strong>
                    </span>
                    <span>
                      <small>{tr("Ingreso diario")}</small>
                      <strong>{formatUsd(Number(position.income))}</strong>
                    </span>
                    <span>
                      <small>{tr("Tasa")}</small>
                      <strong>{String(position.yield).replace(/%$/, "")}%</strong>
                    </span>
                  </div>
                  <div className="finance-position-progress">
                    <span>
                      <small>{tr("Progreso")}</small>
                      <strong>{Math.min(100, Number(position.progress || 0)).toFixed(0)}%</strong>
                    </span>
                    <i>
                      <b
                        style={{
                          width: `${Math.min(100, Number(position.progress || 0))}%`,
                        }}
                      />
                    </i>
                  </div>
                  <div className="finance-position-dates">
                    <span>
                      <CalendarDays /> {position.create_time_r}
                    </span>
                    <span>
                      <CalendarDays /> {position.expire_time_r}
                    </span>
                    {Number(position.state) === 1 && (
                      <strong>
                        {position.remaining_days} {tr("días restantes")}
                      </strong>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
      {!isPreview && (
        <div className="section-heading finance-products-heading">
          <div>
            <p className="eyebrow">{tr("Oportunidades")}</p>
            <h2>{tr("Productos disponibles")}</h2>
          </div>
        </div>
      )}
      {products.isLoading && (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando…")}
        </div>
      )}
      {products.isError && (
        <div className="form-feedback error">
          <CircleAlert /> {tr("No pudimos cargar los productos financieros.")}
        </div>
      )}
      {isPreview ? (
        <div className="empty-state">
          <Landmark />
          <h2>
            {tr("Inicia sesión para consultar los productos disponibles")}
          </h2>
          <Link className="button primary" to="/auth/login">
            {tr("Iniciar sesión")} <ArrowRight />
          </Link>
        </div>
      ) : (
        <div className="finance-grid">
          {(products.data || []).map((item) => (
            <article className="finance-card" key={item.id}>
              <span className="finance-card-icon">
                <CircleDollarSign />
              </span>
              <p className="eyebrow">
                {tr(tab === 1 ? "Producto de staking" : "Producto de fondo")}
              </p>
              <h2>{item.name}</h2>
              <Countdown finishAt={item.finish_times} compact />
              <div className="rate-display">
                <strong>{item.yield}</strong>
                <small>{tr("Rendimiento estimado del ciclo")}</small>
              </div>
              <div className="finance-card-meta">
                <span>
                  <small>{tr("Duración")}</small>
                  <strong>
                    {item.cycle} {tr("días")}
                  </strong>
                </span>
                <span>
                  <small>{tr("Ingreso estimado")}</small>
                  <strong>{formatUsd(Number(item.income))}</strong>
                </span>
                <span>
                  <small>{tr("Límite de compra")}</small>
                  <strong>{item.buy_limit}</strong>
                </span>
                <span>
                  <small>{tr("Inversión")}</small>
                  <strong>{formatUsd(Number(item.money))}</strong>
                </span>
              </div>
              <button
                className="button primary full"
                disabled={item.state !== 1}
                onClick={() => {
                  setCompleted("");
                  setSelected(item);
                }}
              >
                {tr("Comprar")} <ArrowRight />
              </button>
            </article>
          ))}
        </div>
      )}
      {!isPreview && !products.isLoading && !products.data?.length && (
        <div className="empty-state">
          <ShoppingBag />
          <h2>{tr("No hay productos disponibles")}</h2>
        </div>
      )}
      {selected && (
        <div
          className="action-sheet-backdrop"
          role="presentation"
          onClick={() => setSelected(null)}
        >
          <section
            className="action-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="finance-confirm-title"
            onClick={(event) => event.stopPropagation()}
          >
            <span>
              <CircleDollarSign />
            </span>
            <h2 id="finance-confirm-title">{tr("Confirmar inversión")}</h2>
            <p>{selected.name}</p>
            <div className="action-sheet-summary">
              <small>{tr("Total")}</small>
              <strong>{formatUsd(Number(selected.money))}</strong>
            </div>
            {purchase.isError && (
              <div className="form-feedback error">
                <CircleAlert /> {purchase.error.message}
              </div>
            )}
            <div>
              <button
                className="button secondary"
                onClick={() => setSelected(null)}
              >
                {tr("Cancelar")}
              </button>
              <button
                className="button primary"
                disabled={purchase.isPending}
                onClick={() => purchase.mutate()}
              >
                {purchase.isPending ? (
                  <LoaderCircle className="spin" />
                ) : (
                  <ShieldCheck />
                )}{" "}
                {tr("Confirmar")}
              </button>
            </div>
          </section>
        </div>
      )}
      <RemoteNoticeDialog
        open={rulesOpen}
        type={6}
        onClose={() => setRulesOpen(false)}
      />
    </main>
  );
}
